import { LeadListFilter } from "@/types/lead-list-filter.types";

export const CUSTOM_LEAD_LIST_KEY = "custom";

export type PredefinedLeadList = {
    key: string;
    label: string;
    filter: LeadListFilter;
};

export const LEAD_LAYOUT_OPTIONS = ["HT", "HP", "PRP"] as const;

export type LeadLayoutOption = (typeof LEAD_LAYOUT_OPTIONS)[number];

export const SETTING_PRIORITY_MIN = 0;
export const SETTING_PRIORITY_MAX = 500;

/**
 * Predefined setting lists. Each list maps to the backend lead filter used for assignment.
 */
export const PREDEFINED_LEAD_LISTS: PredefinedLeadList[] = [
    {
        key: "dach",
        label: "DACH",
        filter: { languages: ["DE"], layouts: ["HT", "HP"] },
    },
    {
        key: "prp",
        label: "PRP",
        filter: { languages: ["DE"], layouts: ["PRP"] },
    },
    {
        key: "fr",
        label: "FR",
        filter: { languages: ["FR"] },
    },
    {
        key: "es",
        label: "ES",
        filter: { languages: ["ES"] },
    },
    {
        key: "uk",
        label: "UK",
        filter: { languages: ["ENG"] },
    },
];

export const PREDEFINED_LEAD_LIST_KEYS = PREDEFINED_LEAD_LISTS.map((list) => list.key);

export const ALL_LEAD_LIST_KEYS = [...PREDEFINED_LEAD_LIST_KEYS, CUSTOM_LEAD_LIST_KEY] as const;

export const DEFAULT_LEAD_LIST_KEY = PREDEFINED_LEAD_LISTS[0]?.key ?? "dach";

export const getPredefinedLeadListByKey = (key: string): PredefinedLeadList | undefined =>
    PREDEFINED_LEAD_LISTS.find((list) => list.key === key);

export const getLeadListLabel = (listKey: string): string => {
    if (listKey === CUSTOM_LEAD_LIST_KEY) {
        return "Custom";
    }

    return getPredefinedLeadListByKey(listKey)?.label ?? listKey.toUpperCase();
};

export const serializeCommaSeparatedValues = (values: string[] | undefined): string =>
    values?.length ? values.join(",") : "";

export const parseCommaSeparatedValues = (value: string | undefined | null): string[] | undefined => {
    if (!value?.trim()) {
        return undefined;
    }

    const values = value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);

    return values.length ? values : undefined;
};

export const serializeLeadLayouts = (layouts: string[] | undefined): string => serializeCommaSeparatedValues(layouts);

export const parseLeadLayouts = (value: string | undefined | null): string[] | undefined =>
    parseCommaSeparatedValues(value);

export const parseOptionalNumber = (value: string | undefined | null): number | undefined => {
    if (!value?.trim()) {
        return undefined;
    }

    const parsed = Number(value);
    return Number.isInteger(parsed) ? parsed : undefined;
};

export const serializeOptionalNumber = (value: number | undefined): string =>
    value === undefined ? "" : String(value);
